import React, { useState } from 'react';
import {
  Edit,
  FileInput,
  SelectInput,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  maxLength,
  minValue,
  maxValue,
  useTranslate,
  ImageInput,
  ImageField,
  useRefresh,
  useNotify,
  Labeled,
  NumberInput,
  TextField,
  FormDataConsumer
} from 'react-admin';
import Resizer from 'react-image-file-resizer';
import { EC_SITE_TYPE, PREFECTURE_LIST, AUTO_ORDER_APPROVAL_FLAG } from '../common_modules/constants/choices';
import useErrorHandler from '../common_modules/hooks/useErrorHandler';
import NumericRadioButtonGroupInput from '../common_modules/components/NumericRadioButtonGroupInput';
import { Typography, Box, Stack, FormGroup, FormControlLabel, Checkbox, InputAdornment, RadioGroup, Radio } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import CustomUrlField from '../common_modules/components/CustomUrlField';
import EnumTextField from '../common_modules/components/EnumTextField';
import PaymentDateTextField from '../common_modules/components/PaymentDateTextField';
import FormValidation from '../common_modules/components/FormValidation';
import PercentNumberInput from '../common_modules/components/PercentNumberInput';
import FileContentInput from '../common_modules/components/FileContentInput';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { getLocalizableChoices } from '../common_modules/utils/localizeUtils';
import { ProductRewardList } from './settings/ProductRewardSettings';
import { UsageCountRewardRateList } from './settings/UsageCountRewardRateSettings';

/**
 * 既存顧客報酬設定セクション。
 * settings record の `existingCustomerDiscountRate` を直接バインドし、
 * 画面下部の保存ボタン経由で settings エンドポイントに保存する。
 * チェックボックスの ON/OFF は UI ローカル状態で管理し、入力欄を空にしても表示は維持される
 * （注文承認猶予期間と同じパターン）。OFF にしたときに value を null へリセットする。
 */
const ExistingCustomerRewardSection = () => {
  const { setValue, getValues, watch } = useFormContext();
  const initialValue = getValues('existingCustomerDiscountRate');
  const [enabled, setEnabled] = useState<boolean>(
    initialValue !== null && initialValue !== undefined && initialValue !== ''
  );

  // Formの値を取得
  const discountRateValue = watch('existingCustomerDiscountRate');

  // 値が 9999 の場合はパターンA(1)、それ以外（null含む）はパターンB(2)とみなす
  const currentPattern = Number(discountRateValue) === 9999 ? 1 : 2;

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      setValue('existingCustomerDiscountRate', null, { shouldDirty: true });
    }
  };

  const handlePatternChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPattern = parseInt(e.target.value, 10);
    if (newPattern === 1) {
      // パターンA: 報酬を発生させない -> 特殊値 9999 をセット
      setValue('existingCustomerDiscountRate', 9999, { shouldDirty: true });
    } else {
      // パターンB: 減算率を設定 -> クリアしてユーザーに入力させる
      setValue('existingCustomerDiscountRate', null, { shouldDirty: true });
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        marginTop: '10px',
        paddingTop: '10px',
        mb: 2
      }}
    >
      <Typography variant="h6" gutterBottom>
        既存顧客報酬設定
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={enabled}
            onChange={(e) => handleToggle(e.target.checked)}
          />
        }
        label="既存顧客報酬率設定"
      />
      <Typography variant="caption" color="textSecondary" display="block">
        ※ パートナー経由で発生した顧客の初回注文が、ショップの既存顧客であった場合に報酬率を減算します。以後、対象の顧客から注文が発生した場合も同様に適用します。
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block">
        ※ 0.00~1000.00まで入力可能
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block">
        ※ 報酬率の減算を実施しても、最終的な報酬率は0%以下にはなりません。
      </Typography>
      <FormDataConsumer>
        {({ formData }) =>
          [2, 3, 8].includes(Number(formData.ecSiteType)) && (
            <Typography variant="caption" color="textSecondary" display="block">
              ※ 当該カートご利用のユーザー様は、successfeeを利用し始める前の購入者を既存顧客として捕捉することはできません。
            </Typography>
          )
        }
      </FormDataConsumer>
      {enabled && (
        <Box sx={{ mt: 2, ml: 3 }}>
          <RadioGroup
            name="existingCustomerRewardPattern"
            value={currentPattern}
            onChange={handlePatternChange}
          >
            <FormControlLabel
              value={1}
              control={<Radio />}
              label="報酬を発生させない"
            />
            <FormControlLabel
              value={2}
              control={<Radio />}
              label="減算率を設定する"
            />
          </RadioGroup>
          {currentPattern === 2 && (
            <Box sx={{ mt: 1, ml: 3 }}>
              <PercentNumberInput
                source="existingCustomerDiscountRate"
                max={1000}
                validate={[required()]}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// カスタムツールバーを定義して削除ボタンを表示しないようにする
const CustomToolbar = ({ isFormValid }: { isFormValid: boolean }) => (
  <Toolbar style={{ backgroundColor: '#FFFFFF' }}>
    <SaveButton disabled={!isFormValid} /> {/* isFormValid の状態に応じてボタンを無効化 */}
  </Toolbar>
);
interface RewardRow {
  id: number;
  productCode: string; // 今は使わないが構造として定義
  rewardRate: string; // 今は使わないが構造として定義
}

const resizeImageToBase64 = (
  file: File | null,
  maxWidth: number,
  maxHeight: number,
  format: string = 'PNG',
  quality: number = 100
): Promise<string | null> => {
  return new Promise((resolve) => {
    if (!file) {
      resolve(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const widthRatio = maxWidth / img.width;
        const heightRatio = maxHeight / img.height;
        const scaleRatio = Math.min(widthRatio, heightRatio, 1);

        if (scaleRatio === 1) {
          // リサイズ不要
          resolve(event.target?.result as string);
        } else {
          const resizeWidth = img.width * scaleRatio;
          const resizeHeight = img.height * scaleRatio;

          Resizer.imageFileResizer(
            file,
            resizeWidth,
            resizeHeight,
            format,
            quality,
            0,
            (resizedImage) => {
              resolve(resizedImage as string);
            },
            'base64'
          );
        }
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

const ApprovalSettings = () => {
  const translate = useTranslate();
  // useControllerを使ってautoOrderApprovalFlagの値を管理
  const { field } = useController({ name: 'autoOrderApprovalFlag', defaultValue: 0 });

  // 現在の数値から各チェックボックスのON/OFF状態を計算
  const isApproveEnabled = (field.value & 1) > 0;
  const isDisapproveEnabled = (field.value & 2) > 0;

  // チェックボックスが変更されたときの処理
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    let newValue = 0;

    const approveIsOn = name === 'autoApprove' ? checked : isApproveEnabled;
    const disapproveIsOn = name === 'autoDisapprove' ? checked : isDisapproveEnabled;

    if (approveIsOn) {
      newValue += 1;
    }
    if (disapproveIsOn) {
      newValue += 2;
    }
    field.onChange(newValue);
  };

  return (
    <Labeled label={translate('resources.settings.fields.autoOrderApprovalFlag')}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* 自動承認チェックボックス */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }} mb={1}>
          <FormControlLabel
            control={<Checkbox name="autoApprove" checked={isApproveEnabled} onChange={handleChange} />}
            label={translate('resources.settings.fields.autoApproval')}
          />
          <Typography variant="caption" color="textSecondary" gutterBottom>
            注文承認期間を過ぎると注文承認期間内にキャンセルが入った場合を除き、該当注文データの状態は承認となります。
            <br />
          </Typography>
        </Box>

        {/* 自動承認がONの場合のみ猶予期間の入力を表示 */}
        {isApproveEnabled && (
          <Box>
            <NumberInput
              source="orderApprovalGracePeriod"
              validate={[required(), minValue(1), maxValue(99)]}
              min={1}
              max={99}
              InputProps={{
                endAdornment: <InputAdornment position="end">日</InputAdornment>
              }}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (['+', '-', 'e', 'E'].includes(event.key)) {
                  event.preventDefault();
                }
              }}
            />
          </Box>
        )}

        {/* 自動否認チェックボックス */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <FormControlLabel
            control={<Checkbox name="autoDisapprove" checked={isDisapproveEnabled} onChange={handleChange} />}
            label={translate('resources.settings.fields.autoDisapproval')}
          />
          <Typography variant="caption" color="textSecondary" gutterBottom>
            自動否認にチェックを入れるとキャンセルデータ受信時に自動的に該当注文データは否認となります。
            <br />
            チェックが入っていない場合、キャンセルされた注文データは保留となります。
          </Typography>
        </Box>
      </Box>
    </Labeled>
  );
};

export const SettingEdit = () => {
  const translate = useTranslate();
  const handleError = useErrorHandler();
  const refresh = useRefresh();
  const notify = useNotify();

  const [openTermModal, setOpenTermModal] = useState(false);
  const [openContractModal, setOpenContractModal] = useState(false);

  const ecSiteCategoryType = getLocalizableChoices(
    translate,
    `values.ecSiteCategoryType`,
    [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
  );

  const handleTermOpenModal = () => {
    setOpenTermModal(true);
  };

  const handleTermCloseModal = () => {
    setOpenTermModal(false);
  };

  const handleTermConfirmModal = (requestReconsent: boolean) => {
    console.log('OKがクリックされました。再同意を求める:', requestReconsent);
    setOpenTermModal(false);
  };

  const handleContractOpenModal = () => {
    setOpenTermModal(true);
  };

  const handleContractCloseModal = () => {
    setOpenTermModal(false);
  };

  const handleContractConfirmModal = (requestReconsent: boolean) => {
    console.log('OKがクリックされました。再同意を求める:', requestReconsent);
    setOpenTermModal(false);
  };

  const [rows, setRows] = useState<RewardRow[]>([
    // 初期状態で1行表示
    { id: Date.now(), productCode: '', rewardRate: '' }
  ]);
  const handleAddRow = () => {
    const newRow: RewardRow = {
      id: Date.now(),
      productCode: '',
      rewardRate: ''
    };
    setRows((prevRows) => [...prevRows, newRow]);
  };
  const handleTransform = async (data: Record<string, any>) => {
    // 現在の画像データを保持
    const currentStoreLogo = data.storeLogoThumbnail?.src || null;
    const currentBannerImage = data.bannerImage?.src || null;

    // リサイズ処理を実行
    const storeLogoFile = data.storeLogoThumbnail?.rawFile || null;
    const bannerImageFile = data.bannerImage?.rawFile || null;

    const resizedStoreLogo = await resizeImageToBase64(storeLogoFile, 360, 300, 'PNG');
    const resizedBannerImage = await resizeImageToBase64(bannerImageFile, 500, 500, 'PNG');

    return {
      ...data,
      storeLogoThumbnail: resizedStoreLogo || currentStoreLogo,
      bannerImage: resizedBannerImage || currentBannerImage
    };
  };
  // 必須フィールドの定義
  const requiredFields = [
    'name',
    'nameKana',
    'companyName',
    'companyNameKana',
    'representative',
    'representativeKana',
    'email',
    'tel',
    'zipCode',
    'prefecture',
    'city',
    'address1',
    'ecSiteUrl',
    'ecSiteType',
    'ecSiteCategoryType',
    'defaultPartnerReward',
    'autoOrderApprovalFlag'
  ];

  return (
    <Edit
      title={translate('resources.settings.name')}
      redirect={false}
      mutationMode="pessimistic"
      mutationOptions={{
        onError: handleError,
        onSuccess: () => {
          notify('更新しました', { type: 'info' });
          refresh();
        }
      }}
      transform={handleTransform}
    >
      <Box>
        <SimpleForm
          toolbar={
            <FormValidation requiredFields={requiredFields}>
              {(isFormValid) => <CustomToolbar isFormValid={isFormValid} />}
            </FormValidation>
          }
          sx={{
            // maxWidth: '600px'
            width: '100%'
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="name" validate={[required(), maxLength(50)]} fullWidth />
          </Box>
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="nameKana" validate={required()} fullWidth />
          </Box>
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="companyName" validate={required()} fullWidth />
          </Box>
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="companyNameKana" validate={required()} fullWidth />
          </Box>
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="representative" validate={required()} fullWidth />
          </Box>
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="representativeKana" validate={required()} fullWidth />
          </Box>

          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="email" validate={required()} fullWidth />
          </Box>

          <TextInput source="tel" validate={required()} />
          <TextInput source="zipCode" validate={required()} />
          <SelectInput source="prefecture" validate={required()} choices={PREFECTURE_LIST} />

          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="city" validate={required()} fullWidth />
          </Box>
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="address1" validate={required()} fullWidth />
          </Box>

          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="address2" fullWidth helperText={false} />
          </Box>

          <Box
            sx={{
              width: '100%',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              margin: '40px 0px'
            }}
          ></Box>

          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="ecSiteUrl" validate={required()} fullWidth />
          </Box>

          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="ecSiteContactEmail" validate={required()} fullWidth />
          </Box>

          <NumericRadioButtonGroupInput
            source="ecSiteType"
            validate={required()}
            choices={EC_SITE_TYPE}
            helperText="変更するとAPI連携が外れます。変更する際は十分にご注意ください。"
          />

          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <NumericRadioButtonGroupInput
              source="ecSiteCategoryType"
              choices={ecSiteCategoryType}
              validate={required()}
            />
          </Box>

          <Box sx={{ width: '100%', maxWidth: '600px', marginBottom: '16px' }}>
            <Labeled label={translate('resources.settings.fields.agency')}>
              <TextField source="agency" />
            </Labeled>
          </Box>

          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInput source="invoiceNumber" fullWidth />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              {translate('resources.settings.fields.closingDate')}
            </Typography>
            <PaymentDateTextField source="closingDate" label="EEEE" />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              {translate('resources.settings.fields.invoiceDate')}
            </Typography>
            <PaymentDateTextField source="invoiceDate" />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              {translate('resources.settings.fields.plannedPaymentDate')}
            </Typography>
            <EnumTextField source="plannedPaymentMonth" translationKeyPrefix="values.paymentMonth" />
            <PaymentDateTextField source="plannedPaymentDate" />
          </Box>
          <PercentNumberInput
            source="defaultPartnerReward"
            validate={required()}
          />
          <Box
            id="product-reward-list-section"
            sx={{
              width: '100%',
              borderTop: '1px solid rgba(0, 0, 0, 0.1)',
              marginTop: '10px',
              paddingTop: '10px'
            }}
          >
            <Typography variant="h6" gutterBottom>
              商品別報酬設定
            </Typography>
            <ProductRewardList />
          </Box>
          <ExistingCustomerRewardSection />
          <Box
            id="usage-count-reward-rate-list-section"
            sx={{
              width: '100%',
              borderTop: '1px solid rgba(0, 0, 0, 0.1)',
              marginTop: '10px',
              paddingTop: '10px',
              mb: 2
            }}
          >
            <UsageCountRewardRateList />
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              ※ 注文回数は successfee 経由の注文回数をもとに計算します。
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <ApprovalSettings />
          </Box>
          <Box sx={{ mb: 2 }}>
            <CustomUrlField source="inviteCodeUrl" label={translate('resources.settings.fields.inviteCodeUrl')} />
            <ImageField source="qrCode" sx={{ mt: 2 }} />
            <CustomUrlField source="partnerLoginUrl" label={translate('resources.settings.fields.partnerLoginUrl')} />
          </Box>

          <Box
            sx={{
              width: '100%',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              margin: '40px 0px'
            }}
          ></Box>

          <Box display="flex" flexDirection="column" justifyContent="flex-start" sx={{ mb: 2 }}>
            <ImageInput source="storeLogoThumbnail" label="ストアロゴ画像" accept="image/png">
              <ImageField source="src" />
            </ImageInput>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              ストアロゴ画像はPNG形式でアップロードしてください。
              <br />
              幅360px、高さ300px以下にリサイズされます。
            </Typography>
          </Box>

          {/* ★★★ 利用規約ファイルアップロードフィールドを追加 ★★★ */}
          <Box
            sx={{
              width: '100%',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              margin: '40px 0px'
            }}
          ></Box>
        </SimpleForm>
      </Box>
    </Edit>
  );
};

export default SettingEdit;
